import type { ChatMessage, ClanDetail, ClanSummary, CreateClanRequest, FriendRequest, ModeId, ReportRequest, UpdateClanRequest } from '@vm/shared';
import { audio } from '../../game/audio/Audio';
import { api } from '../api/routes';
import { social } from '../api/social';
import { attempt, refreshMe, requireOnline } from './actions';
import { prefs } from './prefs';
import {
  chatChannel,
  chatLog,
  chatOpen,
  chatUnread,
  friends,
  me,
  mutedUsers,
  myClan,
  party,
  presenceById,
  toast,
} from './store';
import { readJson, writeLocal } from '../api/http';

// Friends ------------------------------------------------------------------------------------------------

export async function loadFriends(): Promise<void> {
  const res = await api.friends.get();
  friends.value = res;
  // Fresh data supersedes pushed presence for these users.
  const live = { ...presenceById.value };
  for (const f of res.friends) delete live[f.id];
  presenceById.value = live;
}

export async function sendFriendRequest(req: FriendRequest): Promise<boolean> {
  if (!requireOnline('Adding friends')) return false;
  const res = await attempt(() => api.friends.request(req), { errorTitle: "Couldn't send that request" });
  if (!res) return false;
  friends.value = res;
  audio.play('ui_buy');
  toast('Friend request sent!', { kind: 'success' });
  return true;
}

export async function acceptFriend(userId: string, name?: string): Promise<void> {
  const res = await attempt(() => api.friends.accept(userId), { errorTitle: "Couldn't accept that request" });
  if (!res) return;
  friends.value = res;
  toast(name ? `You and ${name} are now friends!` : 'Friend added!', { kind: 'success' });
}

export async function declineFriend(userId: string): Promise<void> {
  const res = await attempt(() => api.friends.decline(userId));
  if (res) friends.value = res;
}

export async function removeFriend(userId: string): Promise<void> {
  const res = await attempt(() => api.friends.remove(userId), { errorTitle: "Couldn't remove that friend" });
  if (res) friends.value = res;
}

export async function blockUser(userId: string, name: string): Promise<boolean> {
  if (!requireOnline('Blocking')) return false;
  const res = await attempt(() => api.users.block(userId), { errorTitle: `Couldn't block ${name}` });
  if (res === undefined) return false;
  muteUser(userId, true, name);
  toast(`${name} is blocked`, { kind: 'info', body: "You won't see their messages or invites." });
  void loadFriends().catch(() => {});
  return true;
}

export async function unblockUser(userId: string, name: string): Promise<void> {
  const res = await attempt(() => api.users.unblock(userId), { errorTitle: `Couldn't unblock ${name}` });
  if (res === undefined) return;
  muteUser(userId, false);
  toast(`${name} is unblocked`, { kind: 'info' });
  void loadFriends().catch(() => {});
}

export async function submitReport(req: ReportRequest): Promise<boolean> {
  if (!requireOnline('Reporting')) return false;
  const res = await attempt(() => api.reports.create(req), { errorTitle: "Couldn't send that report" });
  if (res === undefined) return false;
  toast('Thanks for the report', { kind: 'success', body: 'Our moderators will take a look.' });
  return true;
}

// Mutes (local only) ---------------------------------------------------------------------------------------

const mutedNames: Record<string, string> = readJson<Record<string, string>>('vm.mutedNames') ?? {};
mutedUsers.value = new Set(Object.keys(mutedNames));

export function mutedName(userId: string): string {
  return mutedNames[userId] ?? 'Player';
}

export function muteUser(userId: string, muted: boolean, name?: string): void {
  if (muted) mutedNames[userId] = name ?? mutedNames[userId] ?? 'Player';
  else delete mutedNames[userId];
  mutedUsers.value = new Set(Object.keys(mutedNames));
  writeLocal('vm.mutedNames', JSON.stringify(mutedNames));
}

// Party ------------------------------------------------------------------------------------------------------

export async function loadParty(): Promise<void> {
  party.value = (await api.party.get()).party;
}

export async function createParty(mode?: ModeId): Promise<boolean> {
  if (!requireOnline('Parties')) return false;
  if (party.value) return true;
  const res = await attempt(() => api.party.create(mode ?? prefs.value.mode), { errorTitle: "Couldn't start a party" });
  if (!res) return false;
  party.value = res;
  return true;
}

/** The party leader's mode choice is shared with the party. */
export async function syncPartyMode(mode: ModeId): Promise<void> {
  const p = party.value;
  if (!p || p.leaderId !== me.value?.id || p.mode === mode) return;
  try {
    party.value = await api.party.create(mode);
  } catch {
    /* members still see the previous mode */
  }
}

export async function inviteToParty(userId: string, name: string, mode?: ModeId): Promise<void> {
  if (!requireOnline('Parties')) return;
  if (!party.value && !(await createParty(mode))) return;
  const res = await attempt(() => api.party.invite(userId), { errorTitle: `Couldn't invite ${name}` });
  if (!res) return;
  party.value = res;
  toast(`Invited ${name} to your party`, { kind: 'success' });
}

export async function joinParty(partyId: string): Promise<void> {
  if (!requireOnline('Parties')) return;
  const res = await attempt(() => api.party.join(partyId), { errorTitle: "Couldn't join that party" });
  if (!res) return;
  party.value = res;
  audio.play('ui_buy');
}

export async function leaveParty(): Promise<void> {
  const res = await attempt(() => api.party.leave(), { errorTitle: "Couldn't leave the party" });
  if (res === undefined) return;
  party.value = null;
}

export async function kickFromParty(userId: string): Promise<void> {
  const res = await attempt(() => api.party.kick(userId), { errorTitle: "Couldn't remove that player" });
  if (res) party.value = res;
}

export async function setPartyReady(ready: boolean): Promise<void> {
  const cur = party.value;
  const myId = me.value?.id;
  if (cur && myId) party.value = { ...cur, members: cur.members.map((m) => (m.id === myId ? { ...m, ready } : m)) };
  const res = await attempt(() => api.party.ready(ready));
  if (res) party.value = res;
  else party.value = cur;
}

// Clans -------------------------------------------------------------------------------------------------------

export async function loadMyClan(): Promise<void> {
  const c = me.value?.clan;
  if (!c) {
    myClan.value = null;
    return;
  }
  myClan.value = await api.clans.get(c.id);
}

function summaryOf(c: ClanDetail): ClanSummary {
  return { id: c.id, name: c.name, tag: c.tag, emblem: c.emblem, level: c.level, members: c.members, maxMembers: c.maxMembers, joinPolicy: c.joinPolicy };
}

export async function createClan(req: CreateClanRequest): Promise<boolean> {
  if (!requireOnline('Clans')) return false;
  const res = await attempt(() => api.clans.create(req), { errorTitle: "Couldn't create your clan" });
  if (!res) return false;
  myClan.value = res;
  if (me.value) me.value = { ...me.value, clan: summaryOf(res) };
  audio.play('levelup');
  toast(`[${req.tag}] ${req.name} is born!`, { kind: 'reward', body: 'Invite your friends to join.' });
  await refreshMe();
  if (!myClan.value) await loadMyClan().catch(() => {});
  return true;
}

export async function joinClan(clan: ClanSummary): Promise<void> {
  if (!requireOnline('Clans')) return;
  const res = await attempt(() => api.clans.join(clan.id), { errorTitle: `Couldn't join ${clan.name}` });
  if (!res) return;
  if (res.status === 'requested') {
    toast('Request sent', { kind: 'success', body: `The officers of ${clan.name} will review it.` });
    return;
  }
  myClan.value = res.clan;
  if (me.value) me.value = { ...me.value, clan: summaryOf(res.clan) };
  audio.play('ui_buy');
  toast(`Welcome to ${clan.name}!`, { kind: 'success' });
  void refreshMe();
}

export async function leaveClan(): Promise<void> {
  const c = me.value?.clan;
  if (!c) return;
  const res = await attempt(() => api.clans.leave(c.id), { errorTitle: "Couldn't leave the clan" });
  if (res === undefined) return;
  myClan.value = null;
  if (me.value) me.value = { ...me.value, clan: null };
  toast(`You left ${c.name}`, { kind: 'info' });
  void refreshMe();
}

export async function disbandClan(): Promise<void> {
  const c = me.value?.clan;
  if (!c) return;
  const res = await attempt(() => api.clans.disband(c.id), { errorTitle: "Couldn't disband the clan" });
  if (res === undefined) return;
  myClan.value = null;
  if (me.value) me.value = { ...me.value, clan: null };
  toast(`${c.name} was disbanded`, { kind: 'info' });
  void refreshMe();
}

export async function updateClan(patch: UpdateClanRequest): Promise<boolean> {
  const c = myClan.value;
  if (!c) return false;
  const res = await attempt(() => api.clans.update(c.id, patch), { errorTitle: "Couldn't save clan settings" });
  if (!res) return false;
  myClan.value = res;
  if (me.value?.clan) me.value = { ...me.value, clan: summaryOf(res) };
  toast('Clan settings saved', { kind: 'success' });
  return true;
}

export async function clanMemberAction(action: 'accept' | 'decline' | 'promote' | 'demote' | 'kick', userId: string, name: string): Promise<void> {
  const c = myClan.value;
  if (!c) return;
  const fns = {
    accept: () => api.clans.acceptRequest(c.id, userId),
    decline: () => api.clans.declineRequest(c.id, userId),
    promote: () => api.clans.promote(c.id, userId),
    demote: () => api.clans.demote(c.id, userId),
    kick: () => api.clans.kick(c.id, userId),
  };
  const res = await attempt(fns[action], { errorTitle: `Couldn't update ${name}` });
  if (!res) return;
  myClan.value = res;
  const msg = { accept: `${name} joined the clan`, decline: `Declined ${name}`, promote: `${name} was promoted`, demote: `${name} was demoted`, kick: `${name} was removed` };
  toast(msg[action], { kind: 'success' });
  if (action === 'promote' && res.roster.find((m) => m.id === me.value?.id)?.role !== 'leader') void refreshMe();
}

// Chat -----------------------------------------------------------------------------------------------------------

const MAX_LINES = 120;

export function appendChat(msg: ChatMessage): void {
  if (mutedUsers.value.has(msg.from.id)) return;
  const log = chatLog.value;
  const lines = log[msg.channel] ?? [];
  if (lines.some((l) => l.id === msg.id)) return;
  chatLog.value = { ...log, [msg.channel]: [...lines, msg].slice(-MAX_LINES) };
  const viewing = chatOpen.value && chatChannel.value === msg.channel;
  if (!viewing && msg.from.id !== me.value?.id) {
    chatUnread.value = { ...chatUnread.value, [msg.channel]: (chatUnread.value[msg.channel] ?? 0) + 1 };
  }
}

export function setChatHistory(channel: string, msgs: ChatMessage[]): void {
  chatLog.value = { ...chatLog.value, [channel]: msgs.filter((m) => !mutedUsers.value.has(m.from.id)).slice(-MAX_LINES) };
}

export function markChannelRead(channel: string): void {
  if (!chatUnread.value[channel]) return;
  const next = { ...chatUnread.value };
  delete next[channel];
  chatUnread.value = next;
}

/** Sends a chat line; restricted accounts may only send quick-chat codes (#q<index>). */
export function sendChat(channel: string, text: string): boolean {
  const m = me.value;
  if (!m) return false;
  if (m.mutedUntil && Date.parse(m.mutedUntil) > Date.now()) {
    toast('You are muted', { kind: 'warning', body: `Chat unlocks ${new Date(m.mutedUntil).toLocaleString()}.` });
    return false;
  }
  const t = text.trim().slice(0, 120);
  if (!t) return false;
  if (m.restricted && !/^#q\d+$/.test(t)) return false;
  if (!social.connected) {
    toast('Chat is reconnecting…', { kind: 'warning' });
  }
  social.send({ t: 'chat', channel: channel as ChatMessage['channel'], text: t });
  return true;
}

export function openChat(channel?: string): void {
  if (channel) chatChannel.value = channel;
  chatOpen.value = true;
  markChannelRead(chatChannel.value);
}

